/**
 * Apoio da "Nova cotacao Pessoa Fisica" (P-NCT-3): a cotacao do leiloeiro do OnPoint
 * (/pf/nova-cotacao; matriz 48, NCT-182 a NCT-203). O tomador e a pessoa fisica pelo
 * CPF, o segurado e a junta comercial da UF e a modalidade e a de leiloeiro.
 *
 * A importancia segurada e "definida conforme a Junta Comercial selecionada", e o valor
 * da garantia por junta nao tem regra aprovada (NCT-202): a geracao fica bloqueada no
 * servidor, e a tela mostra o motivo e as pendencias. Nada aqui calcula. Ver
 * docs/ADR/0066-cotacao-pessoa-fisica-e-leiloeiro.md.
 */

/** Os passos da pessoa fisica, na ordem do codigo do OnPoint (NCT-183). */
export const passosDaPessoaFisica = [
  { key: 'tomador', label: 'Dados do tomador' },
  { key: 'segurado', label: 'Dados do segurado' },
  { key: 'risco', label: 'Dados do risco' },
  { key: 'resumo', label: 'Resumo da cotacao' },
] as const;

export type PassoDaPessoaFisica = (typeof passosDaPessoaFisica)[number]['key'];

export function passoPfValido(valor: string | undefined): PassoDaPessoaFisica {
  return passosDaPessoaFisica.find((passo) => passo.key === valor)?.key ?? 'tomador';
}

/** O rascunho da cotacao de pessoa fisica, como digitado. Nada e gravado alem do tomador. */
export interface RascunhoPf {
  policyholder_id: string;
  policyholder_label: string;
  policyholder_document: string;
  /** UF da junta comercial do passo do segurado. */
  registry_state: string;
  junta_id: string;
  junta_label: string;
  junta_document: string;
  junta_address_label: string;
  modality_id: string;
  coverage_start: string;
  term_days: string;
  coverage_end: string;
  subject: string;
}

export const rascunhoPfVazio: RascunhoPf = {
  policyholder_id: '',
  policyholder_label: '',
  policyholder_document: '',
  registry_state: '',
  junta_id: '',
  junta_label: '',
  junta_document: '',
  junta_address_label: '',
  modality_id: '',
  coverage_start: '',
  term_days: '',
  coverage_end: '',
  subject: '',
};

/** So os digitos do documento digitado. */
export function digitosDoCpf(texto: string): string {
  return texto.replace(/\D/g, '');
}

/**
 * Aviso do campo "Digite o CPF do tomador": onze digitos. O digito verificador e
 * conferido pelo servidor (GET /policyholders/validation), que diz o motivo.
 */
export function avisoDoCpf(texto: string): string | undefined {
  const digitos = digitosDoCpf(texto);
  if (digitos === '') return undefined;
  if (digitos.length !== 11) return 'O CPF tem onze digitos.';
  return undefined;
}

/** O que falta no passo para seguir, na linguagem da tela. */
export function faltasDoPassoPf(
  passo: PassoDaPessoaFisica,
  rascunho: RascunhoPf,
): string[] {
  const faltas: string[] = [];
  if (passo === 'tomador' && !rascunho.policyholder_id) {
    faltas.push('Busque o CPF do tomador ou cadastre o tomador.');
  }
  if (passo === 'segurado') {
    if (!rascunho.registry_state) faltas.push('Selecione a UF da junta comercial.');
    else if (!rascunho.junta_id) faltas.push('Escolha a junta comercial da UF.');
  }
  if (passo === 'risco') {
    if (!rascunho.modality_id) faltas.push('Escolha a modalidade de leiloeiro.');
    if (!rascunho.coverage_start) faltas.push('Informe o inicio da vigencia.');
    if (rascunho.term_days.trim() !== '' && !/^\d+$/.test(rascunho.term_days.trim())) {
      faltas.push('O prazo e um numero inteiro de dias.');
    }
  }
  return faltas;
}

/**
 * O telefone obrigatorio do cadastro da pessoa fisica (NCT-190, "telefone
 * obrigatorio"). O formato do numero nao tem regra no OnPoint lido: so a presenca e
 * conferida, como no cadastro do tomador.
 */
export function avisoDoTelefone(texto: string): string | undefined {
  if (texto.replace(/\D/g, '') === '') return 'O telefone e obrigatorio.';
  return undefined;
}
